import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { DataTransformer } from 'go-modules/models/common/data-transformer';
import type { CourseFolder } from './interfaces/course-folder.interface';
import { Observable } from 'rxjs';
import { CurrentFolderListParams } from '../folder-list-datasource/folder-list-datasource';
import { FolderStats } from '@go-modules/services/course/interfaces/course-folder.interface';
import { NgxGroupService } from '../group/group.service';
import dayjs from 'dayjs';
import { serializeCourseDates } from './course-date-serializer';

export interface FolderStatsResponse {
	[name: string]: FolderStats;
}

@Injectable({
	providedIn: 'root'
})
export class NgxCourseService {
	constructor (
		private http: HttpClient,
		private groupService: NgxGroupService
	) {}

	public getCourses (
		orgId: number,
		params: CurrentFolderListParams = {} as CurrentFolderListParams
	): Observable<CourseFolder[]> {
		let url = `${clientSettings.GoReactV2API}/orgs/${orgId}/folders`;
		const searchParams = new URLSearchParams();

		if (Object.keys(params).length) {
			Object.keys(params).forEach((key) => {
				if (params[key] !== null) {
					searchParams.append(key, params[key]);
				}
			});
			url += `?${searchParams.toString()}`;
		}

		return this.http.get<CourseFolder[]>(url)
			.pipe(map((response: CourseFolder[]) => {
				response.forEach((data) => {
					if (data.end_date) {
						data.end_date = dayjs(data.end_date).utc().format('YYYY-MM-DD HH:mm:ss');
					}
				});

				return response;
			}));
	}

	public getFolderStats (
		orgId: number,
		params: CurrentFolderListParams = {} as CurrentFolderListParams
	): Observable<FolderStatsResponse> {
		let url = `${clientSettings.GoReactV2API}/orgs/${orgId}/folders/stats`;
		const searchParams = new URLSearchParams();

		if (Object.keys(params).length) {
			Object.keys(params).forEach((key) => {
				if (params[key] !== null) {
					searchParams.append(key, params[key]);
				}
			});
			url += `?${searchParams.toString()}`;
		}

		return this.http.get<FolderStatsResponse>(url);
	}

	public saveCourse (payload: any): Observable<any> {
		if (payload.group_id > 0) {
			return this.updateCourse(payload);
		}

		return this.createCourse(payload);
	}

	public createCourse (payload: any): Observable<any> {
		return this.http.post(
			`${clientSettings.GoReactV2API}/accounts/${payload.parent_id}/courses`,
			serializeCourseDates(payload)
		);
	}

	public updateCourse (payload: any): Observable<any> {
		return this.http.put(
			`${clientSettings.GoReactV2API}/courses/${payload.group_id}`,
			serializeCourseDates(payload)
		);
	}

	public updateCourseLicense (courseId: number): Observable<any> {
		return this.http.patch(`${clientSettings.GoReactV2API}/courses/${courseId}/license`, {});
	}

	public archiveCourse (courseId: number): Observable<any> {
		return this.http.patch(`${clientSettings.GoReactV2API}/courses/${courseId}/archive`, {});
	}

	public restoreCourse (courseId: number): Observable<any> {
		return this.http.patch(`${clientSettings.GoReactV2API}/courses/${courseId}/restore`, {});
	}

	public deleteCourse (courseId: number): Observable<any> {
		return this.http.delete(`${clientSettings.GoReactV2API}/folders/${courseId}`);
	}

	public getCourseLicense (courseId: number): Observable<any> {
		return this.groupService.getLicense(courseId)
			.pipe(
				map((license: any) => {
					if (license) {
						['starts_at', 'ends_at'].forEach((prop: string) => license[prop] = DataTransformer.transformToDate(license, prop));
					}

					return license;
				})
			);
	}
}
